import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { SectionDecor } from "@/components/SectionDecor";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { ServicesGrid } from "@/routes/index";
import { company } from "@/data/site";
import { getGroupedCities } from "@/data/cityRegions";
import areasHero from "@/assets/heroes/areas-rooftops.jpg";
import realitiesImg from "@/assets/mckay/roofing-service.webp.asset.json";

const BASE = "https://mckay.steadyautogrowthdemo.com";

export const Route = createFileRoute("/areas/hubs/$hub")({
  loader: ({ params }) => {
    const region = getGroupedCities().find((r) => r.id === params.hub);
    if (!region) throw notFound();
    return region;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Service hub unavailable" }, { name: "robots", content: "noindex" }] };
    }
    const { label, blurb, id, cities } = loaderData;
    const title = `${label} Roofing & Solar Hub – McKay Roofing & Solar`;
    const description = `${blurb} Serving ${cities.length} cities across ${label} with roof repair, roof replacement and solar installation.`;
    const url = `${BASE}/areas/hubs/${id}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: `${label} roofing, ${label} solar, roof repair ${label}, solar installation ${label}, McKay Roofing & Solar` },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-extrabold">Hub not found</h1>
      <p className="mt-3 text-muted-foreground">Try our California service areas page.</p>
      <Link to="/areas-served" className="mt-6 inline-block rounded-md bg-accent px-5 py-3 text-sm font-bold text-accent-foreground">All service areas</Link>
    </div>
  ),
  component: HubPageRoute,
});

function HubPageRoute() {
  const region = Route.useLoaderData();

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={areasHero} alt={`Rooftops across ${region.label} served by McKay Roofing & Solar`} />
          <div className="relative mx-auto flex min-h-[85vh] max-w-5xl flex-col justify-center px-4 py-28 sm:px-6 sm:py-36 lg:px-8 lg:py-44">
            <div className="inline-flex w-fit items-center gap-2 rounded-full border border-primary-foreground/30 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] backdrop-blur">
              <MapPin className="h-3.5 w-3.5" /> {region.label} Hub
            </div>
            <h1 className="mt-6 text-4xl font-extrabold sm:text-5xl lg:text-6xl text-balance">
              Roofing &amp; Solar Across {region.label}
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">{region.blurb}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="btn-glow bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow">
                <Link to="/contact">Get a Free Quote <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact"><Phone className="mr-2 h-4 w-4" />{company.phone}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="bg-muted/40">
          <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div className="relative overflow-hidden rounded-2xl shadow-elegant">
              <img
                src={realitiesImg.url}
                alt={`McKay Roofing & Solar crew working on a roof in ${region.label}`}
                loading="lazy"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-primary sm:text-3xl">What we handle in {region.label}</h2>
              <div className="mt-6 space-y-5 text-base leading-relaxed text-foreground/85">
                <p>
                  We run the same crew and the same standards across all {region.cities.length} cities in this hub, so the person who quotes your roof is the person you see on the job. Repairs, full replacements, and solar installs all go through one team.
                </p>
                <p>
                  Because conditions change from block to block here, we start with an honest look at the roof itself. If a repair holds, we say so. If the deck or underlayment is done, we show you why before anyone talks about a new system on top of it.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <p className="text-sm font-semibold text-muted-foreground">Ready to talk about your project?</p>
                <Button asChild className="btn-glow bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                  <Link to="/contact">Get a Free Quote</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <ServicesGrid />

        {/* Why choose */}
        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Why {region.label} Homeowners Choose McKay</h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              { t: "In business since 1981", d: "Four decades of roofing and solar work across Southern California." },
              { t: "Licensed & insured", d: "California license #1130793. Fully insured for your protection." },
              { t: "Roofing + solar together", d: "One team handles both, so your roof and system work as one." },
              { t: "Honest guidance", d: "We tell you when a repair is enough, with no push to replace." },
              { t: "Local crew", d: "Based in Southern California and on these routes every week." },
              { t: "150+ five-star reviews", d: "Google reviews from homeowners across Southern California." },
            ].map((b) => (
              <div key={b.t} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-primary" />
                <div className="mt-3 text-lg font-bold text-primary">{b.t}</div>
                <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Cities in this hub */}
        <section className="relative overflow-hidden bg-muted/40">
          <SectionDecor variant="house" side="left" tone="primary" opacity={0.05} />
          <div className="relative z-10 mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Cities in the {region.label} Hub</h2>
              <p className="mt-4 text-muted-foreground">Pick your city for local roofing and solar details.</p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {region.cities.map((c) => (
                <Link
                  key={c.slug}
                  to="/areas/california/$city"
                  params={{ city: c.slug }}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-primary hover:shadow-lg"
                >
                  <div className="flex items-center gap-2 text-primary">
                    <MapPin className="h-4 w-4 text-secondary" />
                    <h3 className="font-serif text-xl font-bold">{c.name}</h3>
                  </div>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted-foreground">{c.blurb}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-primary transition-colors group-hover:text-secondary">
                    View city <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/areas/california" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5">
                Back to the California hub <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <CTABlock
          title={`Ready for a free estimate in ${region.label}?`}
          subtitle="Tell us about your project. We usually respond same day."
        />
      </main>
      <SiteFooter />
    </div>
  );
}
